import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { LockClosedIcon } from '@heroicons/react/24/solid';
import api from '@/services/api';
import './AuthPages.css';

interface ValidationError {
  field: string;
  message: string;
}

interface ForgotPasswordResponse {
  status: string;
  message: string;
  data?: {
    reset_token?: string;
  };
}

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [apiMessage, setApiMessage] = useState('');
  const [fade, setFade] = useState(false);
  
  const navigate = useNavigate();

  useEffect(() => {
    setFade(true);
    const root = document.querySelector('#root');
    root?.classList.add('auth-background');

    return () => {
      root?.classList.remove('auth-background');
    };
  }, []);

  const getFieldError = (fieldName: string) => {
    return errors.find(error => error.field === fieldName)?.message;
  };

  const clearFieldError = (fieldName: string) => {
    setErrors(prev => prev.filter(error => error.field !== fieldName));
  };

  const validateForm = (): boolean => {
    const newErrors: ValidationError[] = [];

    if (!email) {
      newErrors.push({ field: 'email', message: 'Email is required' });
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.push({ field: 'email', message: 'Email is invalid' });
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiMessage('');
    setErrors([]);

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await api.post('/auth/forgot-password', { email });
      const result: ForgotPasswordResponse = response.data as ForgotPasswordResponse;

    //   console.log('Forgot password response:', result);

      if (result.status === "success") {
        setApiMessage(result.message || 'Verification code sent to your email!');
        
        // Navigate to OTP verification page with email
        setTimeout(() => {
          setFade(false);
          setTimeout(() => {
            navigate('/verify-otp', { 
              state: { 
                email,
                reset_token: result.data?.reset_token 
              } 
            });
          }, 300);
        }, 1500);
      } else {
        setApiMessage(result.message || 'Failed to send verification code.');
      }
    } catch (error: any) {
      console.error('Forgot password error:', error);
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        if (errorData.errors) {
          const validationErrors: ValidationError[] = [];
          Object.keys(errorData.errors).forEach(field => {
            errorData.errors[field].forEach((message: string) => {
              validationErrors.push({ field, message });
            });
          });
          setErrors(validationErrors);
        } else {
          setApiMessage(errorData.message || 'Failed to send verification code.');
        }
      } else {
        setApiMessage('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`auth-container min-w-full flex-col ${fade ? 'fade-in' : 'fade-out'}`}>
      <h1 className="auth-title mb-8">Forget Password</h1>
      
      {apiMessage && (
        <div className={`message ${apiMessage.includes('success') ? 'message-success' : 'message-error'}`}>
          <div className="message-content">
            {apiMessage}
          </div>
        </div>
      )}

      <div className="auth-card">
        <div className="flex items-center justify-center mb-4">
            <LockClosedIcon className="auth-icon h-32 drop-shadow-[0_5px_5px_rgba(56,136,255,1)]" />
        </div>
        <p className="auth-subtitle font-normal">Please enter your email address to receive a verification code</p>
        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="email" className="form-label text-start">Email</label>
            <input
              id="email"
              type="email"
              className={`form-input placeholder:text-gray-400 rounded-full bg-blue-950/80 ${
                getFieldError('email') ? 'input-error' : ''
              }`}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                clearFieldError('email');
              }}
              placeholder="learner@skillsture.com"
              required
              disabled={isLoading}
            />
            {getFieldError('email') && (
              <div className="error-text">{getFieldError('email')}</div>
            )}
          </div>
          
          <button 
            type="submit" 
            className={`auth-button rounded-full ${isLoading ? 'loading' : ''}`}
            disabled={isLoading}
          >
            {isLoading ? 'Sending...' : 'Send'}
          </button>

          <div className="auth-link">
            <span>Remember your password? </span>
            <Link to="/login" className="auth-link-text">Login</Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;